import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Clock, CheckCircle, Truck, ChevronRight, RotateCcw, Calendar, XCircle } from 'lucide-react';
import { publicOrderService } from '../../../services/api/publicOrderService';
import LoadingSpinner from '../../../components/LoadingSpinner';

const RecentOrdersTab = ({ customerId, supplierId, token, searchQuery = '' }) => {
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState('All');
    const [dateFilter, setDateFilter] = useState('All');

    useEffect(() => {
        const fetchOrders = async () => {
            try {
                setLoading(true);
                const response = await publicOrderService.getCustomerOrders(customerId, supplierId, 0, 50, false);
                const rawOrders = Array.isArray(response) ? response : (response.content || []);
                // Filter to show only pending (non-terminal) orders
                const pendingOrders = rawOrders.filter(order => {
                    const statusUpper = order.status?.toUpperCase();
                    return statusUpper !== 'DELIVERED' && 
                           statusUpper !== 'CANCELLED' && 
                           statusUpper !== 'RETURNED';
                });
                // Sort by latest order first (descending)
                const sortedPending = pendingOrders.sort((a, b) => {
                    const dateA = new Date(a.createdAt || a.orderDate || 0);
                    const dateB = new Date(b.createdAt || b.orderDate || 0);
                    return dateB - dateA;
                });
                setOrders(sortedPending);
                setLoading(false);
            } catch (err) {
                console.error('[RecentOrdersTab] Error fetching orders:', err);
                setError('Failed to load pending orders.');
                setLoading(false);
            }
        };

        if (customerId && supplierId) {
            fetchOrders();
        }
    }, [customerId, supplierId]);

    const getStatusColor = (status) => {
        switch (status?.toUpperCase()) {
            case 'CREATED': return 'bg-sky-50 text-sky-700 border-sky-100';
            case 'ACKNOWLEDGED': return 'bg-indigo-50 text-indigo-700 border-indigo-100';
            case 'PENDING_WAREHOUSE': return 'bg-amber-50 text-amber-700 border-amber-100';
            case 'IN_WAREHOUSE': return 'bg-violet-50 text-violet-700 border-violet-100';
            case 'SENT_TO_WAREHOUSE': return 'bg-purple-50 text-purple-700 border-purple-100';
            case 'DESPATCHED': return 'bg-violet-50 text-violet-700 border-violet-100';
            case 'DELIVERED': return 'bg-emerald-50 text-emerald-700 border-emerald-100';
            case 'CANCELLED': return 'bg-rose-50 text-rose-700 border-rose-100';
            case 'NEW': return 'bg-sky-50 text-sky-700 border-sky-100';
            case 'PROCESSING': return 'bg-yellow-50 text-yellow-700 border-yellow-100';
            case 'CONFIRMED': return 'bg-purple-50 text-purple-700 border-purple-100';
            case 'READY': return 'bg-purple-50 text-purple-700 border-purple-100';
            case 'PACKED': return 'bg-teal-50 text-teal-700 border-teal-100';
            default: return 'bg-slate-50 text-slate-700 border-slate-100';
        }
    };

    const getStatusIcon = (status) => {
        switch (status?.toUpperCase()) {
            case 'CREATED': case 'NEW': return <Package className="w-4 h-4" />;
            case 'ACKNOWLEDGED': return <CheckCircle className="w-4 h-4" />;
            case 'PENDING_WAREHOUSE': case 'PROCESSING': case 'IN_WAREHOUSE': case 'SENT_TO_WAREHOUSE': return <Clock className="w-4 h-4" />;
            case 'DESPATCHED': return <Truck className="w-4 h-4" />;
            case 'DELIVERED': case 'READY': case 'CONFIRMED': case 'PACKED': return <CheckCircle className="w-4 h-4" />;
            case 'CANCELLED': return <XCircle className="w-4 h-4" />;
            default: return <Package className="w-4 h-4" />;
        }
    };

    const formatOrderDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const datePart = date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
        const timePart = date.toLocaleTimeString(undefined, {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
        return `${datePart} • ${timePart}`;
    };

    const handleReorder = (order) => {
        if (!order || !order.items || !Array.isArray(order.items)) return;
        
        const cartItems = order.items.map(item => ({
            id: item.productPublicId || item.productId,
            name: item.productName || item.name,
            price: item.unitPrice || item.price || 0,
            quantity: item.quantity || 1,
            unit: item.unit || 'pcs',
            originalPrice: item.unitPrice || item.price || 0,
            hasDiscount: false
        }));

        localStorage.setItem('yaseero_reorder_items', JSON.stringify(cartItems));
        navigate(`/shop?token=${token}&openCart=true`);
    };

    if (loading) {
        return <div className="py-12"><LoadingSpinner message="Loading your pending orders..." /></div>;
    }

    if (error) {
        return <div className="py-8 text-center text-red-500">{error}</div>;
    }

    const filteredOrders = orders.filter(order => {
        // 1. Search Query Filter
        const query = searchQuery.trim().toLowerCase();
        let matchesQuery = true;
        if (query) {
            const matchesRef = order.orderRefNo?.toLowerCase().includes(query) || 
                               order.publicId?.toLowerCase().includes(query);
            const matchesStatusName = order.status?.toLowerCase().includes(query);
            matchesQuery = matchesRef || matchesStatusName;
        }

        // 2. Status Filter
        let matchesStatus = true;
        if (statusFilter !== 'All') {
            const orderStatusUpper = order.status?.toUpperCase() || '';
            if (statusFilter === 'NEW') {
                matchesStatus = orderStatusUpper === 'NEW' || orderStatusUpper === 'CREATED' || orderStatusUpper === 'ACKNOWLEDGED';
            } else if (statusFilter === 'PROCESSING') {
                matchesStatus = orderStatusUpper === 'PROCESSING' || orderStatusUpper === 'PENDING_WAREHOUSE' || orderStatusUpper === 'IN_WAREHOUSE' || orderStatusUpper === 'SENT_TO_WAREHOUSE';
            } else if (statusFilter === 'DESPATCHED') {
                matchesStatus = orderStatusUpper === 'DESPATCHED' || orderStatusUpper === 'READY' || orderStatusUpper === 'CONFIRMED' || orderStatusUpper === 'DISPATCHED' || orderStatusUpper === 'PACKED';
            } else {
                matchesStatus = orderStatusUpper === statusFilter;
            }
        }

        // 3. Date Filter
        let matchesDate = true;
        if (dateFilter !== 'All') {
            const orderTime = new Date(order.createdAt || order.orderDate || 0).getTime();
            const nowTime = new Date().getTime();
            const diffDays = (nowTime - orderTime) / (1000 * 60 * 60 * 24);

            if (dateFilter === 'Today') {
                const todayStr = new Date().toDateString();
                const orderDayStr = new Date(order.createdAt || order.orderDate || 0).toDateString();
                matchesDate = todayStr === orderDayStr;
            } else if (dateFilter === '7days') {
                matchesDate = diffDays <= 7;
            } else if (dateFilter === '30days') {
                matchesDate = diffDays <= 30;
            } else if (dateFilter === '90days') {
                matchesDate = diffDays <= 90;
            }
        }

        return matchesQuery && matchesStatus && matchesDate;
    });

    return (
        <div className="w-full max-w-4xl mx-auto pb-28">
            {/* Filters Bar */}
            <div className="flex flex-wrap gap-3 mb-4 items-center justify-between px-1">
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                    {filteredOrders.length} pending {filteredOrders.length === 1 ? 'order' : 'orders'} found
                </span>
                
                <div className="flex gap-2 w-full sm:w-auto">
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="flex-1 sm:flex-none px-3 py-1.5 rounded-xl text-xs font-bold bg-white border border-slate-100 hover:border-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500 shadow-sm"
                    >
                        <option value="All">All Statuses</option>
                        <option value="NEW">New</option>
                        <option value="PROCESSING">Processing</option>
                        <option value="DESPATCHED">Ready / Despatched</option>
                    </select>
                    
                    <select
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                        className="flex-1 sm:flex-none px-3 py-1.5 rounded-xl text-xs font-bold bg-white border border-slate-100 hover:border-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500 shadow-sm"
                    >
                        <option value="All">All Time</option>
                        <option value="Today">Today</option>
                        <option value="7days">Last 7 Days</option>
                        <option value="30days">Last 30 Days</option>
                        <option value="90days">Last 90 Days</option>
                    </select>
                </div>
            </div>

            {filteredOrders.length === 0 ? (
                <div className="text-center py-16 bg-white border border-slate-100 rounded-[2rem] p-8 shadow-sm max-w-md mx-auto mt-4">
                    <div className="bg-slate-50 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-slate-100">
                        <Package className="w-8 h-8 text-slate-400" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-1">No Orders Found</h3>
                    <p className="text-slate-400 text-sm">We couldn't find any pending orders matching your filters.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredOrders.map(order => (
                        <div 
                            key={order.publicId}
                            onClick={() => navigate(`/status/${order.publicId}?token=${token}&from=pending`)}
                            className="w-full bg-white rounded-3xl p-5 text-left border border-slate-100/80 shadow-sm shadow-slate-100 hover:border-cyan-200 hover:shadow-lg transition-all group relative overflow-hidden flex flex-col justify-between gap-4 min-h-[160px] cursor-pointer"
                        >
                            <div className="flex justify-between items-start">
                                <div>
                                    <div className="flex items-center gap-1.5 text-slate-400 mb-1">
                                        <Calendar className="w-3.5 h-3.5" />
                                        <span className="text-[11px] font-bold uppercase tracking-wider">
                                            {formatOrderDate(order.createdAt || order.orderDate)}
                                        </span>
                                    </div>
                                    <p className="font-black text-slate-900 text-2xl md:text-3xl mt-1">
                                        #{order.orderRefNo || order.publicId.substring(0, 8)}
                                    </p>
                                </div>
                                <div className={`px-3 py-1.5 rounded-xl border text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 shadow-sm ${getStatusColor(order.status)}`}>
                                    {getStatusIcon(order.status)}
                                    {order.status}
                                </div>
                            </div>

                            {/* Items preview inside the card */}
                            {order.items && order.items.length > 0 && (
                                <div className="w-full text-xs text-slate-500 space-y-2.5 py-3 px-1 border-t border-slate-50/80">
                                    {order.items.slice(0, 3).map((item, idx) => (
                                        <div key={idx} className="flex justify-between items-center gap-2">
                                            <span className="truncate max-w-[220px] font-bold text-slate-800 text-sm">{item.productName || item.name}</span>
                                            <span className="font-black text-cyan-700 bg-cyan-50 border border-cyan-100/50 px-2.5 py-1 rounded-xl text-xs whitespace-nowrap shadow-sm flex items-center gap-1.5">
                                                <span>Qty: {item.quantity}</span>
                                                {item.unit && (
                                                    <span className="inline-block px-1.5 py-0.5 bg-cyan-100/60 text-cyan-800 rounded text-[9px] font-bold">
                                                        {item.unit}
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                    ))}
                                    {order.items.length > 3 && (
                                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider pl-0.5 pt-1">
                                            + {order.items.length - 3} more items
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            <div className="w-full flex items-center justify-between border-t border-slate-50 pt-4 mt-auto">
                                <div>
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Total Amount</p>
                                    <p className="font-black text-cyan-600 text-xl">AED {order.totalAmount?.toFixed(2)}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleReorder(order);
                                        }}
                                        className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-cyan-50 text-cyan-600 hover:bg-cyan-100 transition-colors text-xs font-bold border border-cyan-100"
                                    >
                                        <RotateCcw className="w-3.5 h-3.5" />
                                        Reorder
                                    </button>
                                    <div className="flex items-center gap-1 text-xs font-bold text-slate-500 group-hover:text-cyan-600 transition-colors bg-slate-50 group-hover:bg-cyan-50 px-3 py-2 rounded-xl">
                                        View Track <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default RecentOrdersTab;
